import { Add } from "assets/icons/Common";
import { Row } from "components/primitives";
import { DashboardContext } from "contexts/DashboardContext";
import { useCallback, useContext, useEffect, useState, FC } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { CurrentScheduleSettingItem } from "./CurrentScheduleSettingItem";
import { EditActivityModal } from "./EditActivityModal";
import { ScheduleItemData } from "./interfaces";
import { SavedSchedule } from "./OrganiseScheduleSetting";
import { FooterButton, Header, OuterContainer, ScheduleContainer } from "./scheduleSetting.styles";

interface Props {
  savedActivities: ScheduleItemData[];
  onSubmit: (newSavedActivities: ScheduleItemData[]) => void;
  onAddItemToSchedule: (id: string) => void;
}

export const SavedActivities: FC<Props> = ({ savedActivities, onSubmit, onAddItemToSchedule }) => {
  const {
    data: { assignedSchedules },
    setData: updateGlobalData,
  } = useContext(DashboardContext);
  const [showModal, setShowModal] = useState(false);
  const [selectedActivityIndex, setSelectedActivityIndex] = useState<number | undefined>();
  const [newSavedActivities, setNewSavedActivities] = useState(savedActivities);

  useEffect(() => {
    updateGlobalData({ savedActivities: newSavedActivities });
  }, [newSavedActivities]);

  const addItemPressed = () => {
    setSelectedActivityIndex(undefined);
    setShowModal(true);
  };

  function updateActivityById(
    assignedSchedules: { [date: string]: SavedSchedule },
    activityId: string,
    newActivity: ScheduleItemData,
  ) {
    for (const date in assignedSchedules) {
      const schedule = assignedSchedules[date];

      // Assuming schedule.activities is an array of activity objects
      const activityIndex = schedule.schedule.findIndex((act) => act.id === activityId);

      if (activityIndex !== -1) {
        const link = schedule.schedule[activityIndex].link;
        const time = schedule.schedule[activityIndex].time;
        // Mutate the specific activity directly
        schedule.schedule[activityIndex] = {
          ...schedule.schedule[activityIndex],
          ...newActivity,
          link: link,
          time: time,
        };
      }
    }
  }

  const handleActivitySave = (newActivityData: ScheduleItemData) => {
    if (selectedActivityIndex !== undefined) {
      let newData = [...newSavedActivities];
      updateActivityById(assignedSchedules, newActivityData.id, newActivityData);
      newData[selectedActivityIndex] = newActivityData;
      setNewSavedActivities(newData);
      setSelectedActivityIndex(undefined);
      onSubmit(newData);
    } else {
      let newData = [...newSavedActivities, newActivityData];
      setNewSavedActivities((prevActivities: ScheduleItemData[]) => [
        ...prevActivities,
        newActivityData,
      ]);
      onSubmit(newData);
    }
  };

  const selectScheduleItem = (index: number) => {
    setSelectedActivityIndex(index);
    setShowModal(true);
  };

  const deleteScheduleItem = (index: number) => {
    let newData = [...newSavedActivities];
    newData.splice(index, 1);
    setNewSavedActivities(newData);
    onSubmit(newData);
  };

  const handleUpdateOrder = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const newData = [...newSavedActivities];
      newData.splice(dragIndex, 1);
      newData.splice(hoverIndex, 0, newSavedActivities[dragIndex]);

      setNewSavedActivities(newData);
    },
    [newSavedActivities, setNewSavedActivities],
  );

  return (
    <OuterContainer>
      <Header>Activity Bank</Header>
      <DndProvider backend={HTML5Backend} context={window}>
        <ScheduleContainer>
          {newSavedActivities &&
            newSavedActivities.map((scheduleItem, index) => {
              if (scheduleItem) {
                const handleClick = () => selectScheduleItem(index);
                const handleDelete = () => deleteScheduleItem(index);
                const handleAdd = () => onAddItemToSchedule(scheduleItem.id);

                return (
                  <CurrentScheduleSettingItem
                    key={scheduleItem.id}
                    item={scheduleItem}
                    onUpdateOrder={handleUpdateOrder}
                    onDelete={handleDelete}
                    onClick={handleClick}
                    index={index}
                    dataLength={newSavedActivities.length}
                    onAdd={handleAdd}
                  />
                );
              } else {
                return null;
              }
            })}
        </ScheduleContainer>
      </DndProvider>
      <FooterButton onClick={addItemPressed}>
        <Row>
          Add new activity <Add />
        </Row>
      </FooterButton>
      {showModal && (
        <EditActivityModal
          activity={
            selectedActivityIndex !== undefined
              ? newSavedActivities[selectedActivityIndex]
              : undefined
          }
          hasClosed={() => setShowModal(false)}
          onSubmit={handleActivitySave}
          isSavedActivity={true}
        />
      )}
    </OuterContainer>
  );
};
